import { FetchState, Payor } from "@/shared/types";
import { EmployerFeeSchedule, EmployerFeeScheduleState } from "@/employer/types";
import { getEmployerFeeSchedules, getEmployerFeeSchedulesActiveForUser } from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { defineStore } from "pinia";
import { formatCurrency } from "@/shared/currency";

export const initialState: EmployerFeeScheduleState = {
    fetchState: FetchState.Uninitialized,
    username: "",
    employerFeeSchedules: []
};

export const useEmployerFeeScheduleStore = defineStore("employerFeeSchedule", {
    state: (): EmployerFeeScheduleState => (_.cloneDeep(initialState)),
    getters: {
        isUninitialized: state => state.fetchState === FetchState.Uninitialized,
        isLoaded: state => state.fetchState === FetchState.Loaded,
        isLoading: state => state.fetchState === FetchState.Loading,
        isFailed: state => state.fetchState === FetchState.Failed,
        getEmployerFeeSchedulesAsTableItems: state => state.employerFeeSchedules.map(x => mapEmployerFeeScheduleTableItem(x)),
        getFeeScheduleTableItemsForAccountholder: state => state.employerFeeSchedules
            .filter(f => f.whoPays === Payor.Accountholder)
            .map(f => mapEmployerFeeScheduleTableItem(f))
    },
    actions: {
        setEmployerFeeSchedules (employerFeeSchedules: EmployerFeeSchedule[]) {
            this.employerFeeSchedules = [...employerFeeSchedules];
        },
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        loadEmployerFeeSchedules (args?: { username: string, partnerId: number, offeringId: number }) {
            if (args !== undefined && this.username === args.username) {
                return;
            } else if (args === undefined && this.fetchState === FetchState.Loaded) {
                return;
            }

            this.employerFeeSchedules = [];
            this.fetchState = FetchState.Loading;

            getEmployerFeeSchedules(args)
                .then(response => {
                    this.username = args !== undefined ? args.username : "";
                    this.fetchState = FetchState.Loaded;
                    this.employerFeeSchedules = response.data.employerFeeSchedules;
                })
                .catch(error => {
                    this.employerFeeSchedules = [];
                    this.fetchState = FetchState.Failed;
                    return storeHelper.handleError({ error, rethrow: false });
                });
        },
        async loadEmployerFeeSchedulesActiveForUser (): Promise<any> {
            if (this.fetchState === FetchState.Loaded) {
                return Promise.resolve();
            }

            this.setFetchState(FetchState.Loading);

            getEmployerFeeSchedulesActiveForUser()
                .then(response => {
                    this.setFetchState(FetchState.Loaded);
                    this.setEmployerFeeSchedules(response.data.employerFeeSchedules);
                })
                .catch(error => {
                    this.setFetchState(FetchState.Failed);
                    return storeHelper.handleError({ error, rethrow: false });
                });
        },
    }
});

export interface EmployerFeeScheduleTableItem {
    feeType: string;
    amount: string;
    waiver: string;
    whoPaysDisplay: string;
    whoPays: Payor;
}

const employerFeeSchedulePayorDisplayMap: Record<Payor, string> = {
    unknown: "",
    accountholder: "Paid by accountholder",
    employer: "Paid by employer"
};

const mapEmployerFeeScheduleTableItem = (fee: EmployerFeeSchedule): EmployerFeeScheduleTableItem => {
    return {
        feeType: fee.feeName,
        waiver: fee.balanceWaiver ? `Fee will be waived when the average daily balance exceeds ${formatCurrency(fee.balanceWaiver)}` : "",
        amount: mapEmployerFeeScheduleAmountForDisplay(fee.feeAmount),
        whoPaysDisplay: employerFeeSchedulePayorDisplayMap[fee.whoPays],
        whoPays: fee.whoPays
    };
};

const mapEmployerFeeScheduleAmountForDisplay = (feeAmount: number): string => {
    const amount = formatCurrency(feeAmount);
    return `${amount}`;
};